import * as yup from "yup";

import { useRouter } from "next/router";
import useUser from "grandus-lib/hooks/useUser";
import useCart from "grandus-lib/hooks/useCart";

import { Form, Button, Row, Col, Divider } from "antd";
import { useFormikContext, Formik } from "formik";
import get from "lodash/get";
import forEach from "lodash/forEach";
import isFunction from "lodash/isFunction";
import upperFirst from "lodash/upperFirst";

import TextInput from "grandus-lib/components-atomic/form/TextInput";
import PasswordInput from "grandus-lib/components-atomic/form/PasswordInput";
import useWebInstance from "grandus-lib/hooks/useWebInstance";

import { FacebookFilled, GoogleOutlined } from "@ant-design/icons";
import SalesManago from "utils/salesmanago";
import { useTranslation } from "next-i18next";

const Register = ({ afterRegisterCallback = null }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { createUser } = useUser();
  const { cart } = useCart();
  const { webInstance, settings, domain } = useWebInstance();
  const facebokLoginEnabled = get(settings, "facebook_login_enabled");
  const googleLoginEnabled = get(settings, "google_login_enabled");
  const formProps = {
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      passwordConfirmation: "",
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .nullable()
        .email(upperFirst(t('form:enter email in email@email.com format')))
        .required(upperFirst(t('form:required field'))),
      password: yup
        .string()
        .min(6, `${upperFirst(t('user:password'))} ${t('form:has to be longer than {{ count }} chars', { count: 6 })}`)
        .required(upperFirst(t('form:required field'))),
      passwordRepeat: yup
        .string()
        .oneOf([yup.ref("password"), null], `${upperFirst(t('user:passwords'))} ${t('form:are not equal')}`)
        .required(upperFirst(t('form:required field'))),
    }),
    onSubmit: async (values, { errors, setFieldError }) => {
      const userData = {
        user: {
          firstname: get(values, "firstname", ""),
          surname: get(values, "surname", ""),
          email: get(values, "email"),
          password: get(values, "password"),
          passwordRepeat: get(values, "passwordRepeat"),
        },
      };
      if (cart?.accessToken) {
        userData.cart = {
          accessToken: cart?.accessToken,
        };
      }

      await createUser(userData, (newUser) => {
        if (!get(newUser, "success", true)) {
          forEach(get(newUser, "data.messages", []), (res) => {
            setFieldError(get(res, "field"), get(res, "message"));
          });
        } else {
          if (isFunction(afterRegisterCallback)) {
            afterRegisterCallback(newUser);
          }
        }
        if (newUser?.data?.attributes) {
          SalesManago.contactUpsert(get(newUser, "data.attributes"));
        }
      });

      return true;
    },
  };

  return (
    <>
      <Row gutter={[8, 24]}>
        {facebokLoginEnabled ? (
          <Col xs={24} md={googleLoginEnabled ? 12 : 24}>
            <Button
              type="primary"
              icon={<FacebookFilled />}
              style={{ background: "#4267B2", borderColor: "#4267B2" }}
              href={`${
                process.env.NEXT_PUBLIC_OAUTH_HOST
              }/api/v2/auth/facebook?webInstanceToken=${get(
                webInstance,
                "webInstanceToken"
              )}&cartAccessToken=${encodeURIComponent(
                get(cart, "accessToken", "")
              )}&frontendReturnUrl=${domain}/api/lib/v1/auth/oauth?backUrl=${
                router.asPath
              }`}
              block
            >
              Facebook prihlásenie
            </Button>
          </Col>
        ) : null}

        {googleLoginEnabled ? (
          <Col xs={24} md={facebokLoginEnabled ? 12 : 24}>
            <Button
              type="primary"
              icon={<GoogleOutlined />}
              style={{ background: "#db3236", borderColor: "#db3236" }}
              href={`${
                process.env.NEXT_PUBLIC_OAUTH_HOST
              }/api/v2/auth/google?webInstanceToken=${get(
                webInstance,
                "webInstanceToken"
              )}&cartAccessToken=${encodeURIComponent(
                get(cart, "accessToken", "")
              )}&frontendReturnUrl=${domain}/api/lib/v1/auth/oauth?backUrl=${
                router.asPath
              }`}
              block
            >
              Google prihlásenie
            </Button>
          </Col>
        ) : null}
      </Row>
      {facebokLoginEnabled || googleLoginEnabled ? <Divider /> : ""}
      <Formik {...formProps}>
        <RegisterForm />
      </Formik>
    </>
  );
};

const RegisterForm = () => {
  const { t } = useTranslation();
  const {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormikContext();

  return (
    <Form wrapperCol={{ span: 24 }} onFinish={handleSubmit}>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <TextInput
            label={upperFirst(t('user:firstname'))}
            name="firstname"
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Col>
        <Col xs={24} md={12}>
          <TextInput
            label={upperFirst(t('user:lastname'))}
            name="surname"
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Col>
      </Row>
      <TextInput
        label={upperFirst(t('user:email'))}
        name="email"
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        handleBlur={handleBlur}
      />
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <PasswordInput
            label={upperFirst(t('user:password'))}
            name="password"
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Col>
        <Col xs={24} md={12}>
          <PasswordInput
            label={upperFirst(t('user:password confirm'))}
            name="passwordRepeat"
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Col>
      </Row>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={!isValid}
          loading={isValid && isSubmitting}
        >
          {upperFirst(t('user:register'))}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default Register;
